import React, { useCallback, useMemo, useState } from "react";
import MultiSelectInputDashboard, {
  Option,
} from "../../../components/MultiSelectInputDashboard";
import SingleSelectInput, {
  Option as SOption,
} from "../../../components/SingleSelectInput";
import SearchButton from "../../../components/SearchButton";
import RefreshButton from "../../../components/RefreshButton";
import {
  useGetFranchiseTypes,
  useGetCarrierNames,
  useGetOfficeNames,
  useGetAcquisitionNames,
  useGetBrandNames,
  useGetStates,
  useGetDistributionChannels,
} from "./hooks/useGetDashboardFilterOptions";
import { useRecoilValue } from "recoil";
import { accountAtom } from "../../Login/atoms/AccountAtom";

export type DashboardFiltersPayload = {
  franchiseType?: Option;
  carrier?: Option;
  acquisition?: Option;
  brand?: Option[];
  offices?: Option[];
  states?: string[];
  inProduction?: Option;
  channel?: Option;
};

type Props = {
  officeOptions?: Option[];
  channelOptions?: Option[];
  stateOptions?: Option[];
  franchiseOptions?: Option[];
  carrierOptions?: Option[];
  acquisitionOptions?: Option[];
  brandOptions?: Option[];
  inProductionOptions?: Option[];
  initialFranchise?: Option | number | string | null;
  initialCarrier?: Option | number | string | null;
  initialAcquisition?: Option | number | string | null;
  initialBrand?: Option[] | number[] | string[];
  initialInProduction?: Option | number | string | null;
  initialOffices?: Option[] | number[] | string[];
  initialStates?: Option[] | number[] | string[];
  initialChannel?: Option[] | number[] | string[];
  onApply?: (payload: DashboardFiltersPayload) => void;
  onClear?: () => void;
  className?: string;
};

const DashboardFilterUI: React.FC<Props> = ({
  officeOptions,
  channelOptions,
  stateOptions,
  franchiseOptions,
  carrierOptions,
  acquisitionOptions,
  brandOptions,
  inProductionOptions,
  initialFranchise = null,
  initialCarrier = null,
  initialAcquisition = null,
  initialBrand = [],
  initialInProduction = null,
  initialOffices = [],
  initialStates = [],
  initialChannel = null,
  onApply,
  onClear,
  className = "",
}) => {
  const { data: franchiseData } = useGetFranchiseTypes();
  const { data: carrierData } = useGetCarrierNames();
  const { data: officeData } = useGetOfficeNames();
  const { data: acquisitionData } = useGetAcquisitionNames();
  const { data: brandData } = useGetBrandNames();
  const { data: statesData } = useGetStates();
  const { data: channelData } = useGetDistributionChannels();

  const mapFranchise = useCallback((item: any): Option => {
    const value = item?.franchiseTypeId ?? item?.id ?? item?.value ?? item;
    const label =
      item?.franchiseTypeName ?? item?.name ?? item?.label ?? String(item);
    return { label: String(label).trim(), value: String(value) };
  }, []);

  const mapCarrier = useCallback((item: any): Option => {
    const value = item?.masterCarrierId ?? item?.id ?? item?.value ?? item;
    const label =
      item?.carrierName ?? item?.name ?? item?.label ?? String(item);
    return { label: String(label).trim(), value: String(value) };
  }, []);

  const mapOffice = useCallback((item: any): Option => {
    const officeCode = item?.officeCode ?? item?.OfficeCode ?? null;
    const fallbackId = item?.officeId ?? item?.id ?? item?.value ?? item;

    const officeName = item?.officeName ?? item?.officeCode ?? String(item);
    let officeCodeSuffix = "";
    if (officeCode) {
      officeCodeSuffix = ` (${officeCode})`;
    } else if (item?.officeCode) {
      officeCodeSuffix = ` (${item.officeCode})`;
    }
    const city = item?.city ? ` — ${item.city}` : "";
    let stateCode = "";
    if (item?.stateCode) {
      stateCode = `, ${item.stateCode}`;
    } else if (item?.state) {
      stateCode = `, ${item.state}`;
    }

    const label = `${officeName}${officeCodeSuffix}${city}${stateCode}`.trim();

    const value = officeCode ? String(officeCode).trim() : String(fallbackId);

    return { label: String(label), value };
  }, []);

  const mapAcquisition = useCallback((item: any): Option | null => {
    const raw =
      item?.AcquisitionName ??
      item?.acquisitionName ??
      item?.name ??
      item?.label ??
      item;
    const name = raw != null ? String(raw).trim() : "";
    return name ? { label: name, value: name } : null;
  }, []);

  const mapInProduction = useCallback((item: any): Option => {
    const value = item?.value ?? item;
    const label = item?.label ?? String(item);
    return { label: String(label).trim(), value: String(value) };
  }, []);

  const getFirstDefined = (item: any, keys: string[], fallback = "") => {
    for (const key of keys) {
      if (item?.[key] != null && item?.[key] !== "") return item[key];
    }
    return fallback;
  };

  const mapBrand = useCallback((item: any): Option => {
    const brandKeys = [
      "brandMarketingName",
      "brandName",
      "brandDescription",
      "label",
    ];

    const rawBrand = getFirstDefined(item, brandKeys);

    return {
      label: String(rawBrand).trim(),
      value: String(rawBrand).trim(),
    };
  }, []);

  const mapState = useCallback((item: any): Option => {
    const codeRaw =
      item?.states ?? item?.state ?? item?.stateCode ?? item?.code ?? "";
    const code = String(codeRaw ?? "").trim();

    const label =
      item?.description ??
      item?.state ??
      item?.states ??
      item?.label ??
      String(item);

    return {
      label: String(label).trim(),
      value: code,
    };
  }, []);

  const mapChannels = useCallback((item: any): Option => {
    const value = item?.channelId;
    const label = item?.channelName;
    return { label: String(label).trim(), value: String(value) };
  }, []);

  const account = useRecoilValue(accountAtom) as any | null;

  const apiFranchiseOptions: Option[] =
    Array.isArray(franchiseData) && franchiseData.length
      ? franchiseData.map(mapFranchise)
      : franchiseOptions ?? [];

  const allOption: Option = { label: "All", value: "" };
  const confieOption: Option = { label: "Confie", value: String(3) };

  const franchiseOptionsMemo = useMemo<Option[]>(() => {
    const isFr = !!account?.isFranchise;
    const isNonFr = !!account?.isNonFranchise;
    if (
      !account ||
      (account.isFranchise === undefined &&
        account.isNonFranchise === undefined)
    ) {
      const seen = new Set(apiFranchiseOptions.map((o) => String(o.value)));
      const base: Option[] = [];
      if (!seen.has(String(allOption.value))) base.push(allOption);
      if (!seen.has(String(confieOption.value))) base.push(confieOption);
      return [
        ...base,
        ...apiFranchiseOptions.map((a) => ({ ...a, value: String(a.value) })),
      ];
    }

    if (isFr && !isNonFr) {
      const seen = new Set(apiFranchiseOptions.map((o) => String(o.value)));
      const base: Option[] = [];
      if (!seen.has(String(allOption.value))) base.push(allOption);
      return [
        ...base,
        ...apiFranchiseOptions.map((a) => ({ ...a, value: String(a.value) })),
      ];
    }

    if (isFr && isNonFr) {
      const seen = new Set(apiFranchiseOptions.map((o) => String(o.value)));
      const base: Option[] = [];
      if (!seen.has(String(allOption.value))) base.push(allOption);
      if (!seen.has(String(confieOption.value))) base.push(confieOption);
      return [
        ...base,
        ...apiFranchiseOptions.map((a) => ({ ...a, value: String(a.value) })),
      ];
    }

    if (!isFr && isNonFr) {
      return [allOption];
    }

    const seen = new Set(apiFranchiseOptions.map((o) => String(o.value)));
    const base: Option[] = [];
    if (!seen.has(String(allOption.value))) base.push(allOption);
    return [
      ...base,
      ...apiFranchiseOptions.map((a) => ({ ...a, value: String(a.value) })),
    ];
  }, [franchiseData, franchiseOptions, mapFranchise, account]);

  const defaultFranchiseOption = useMemo<Option | null>(() => {
    if (!franchiseOptionsMemo || franchiseOptionsMemo.length === 0) return null;
    return (
      franchiseOptionsMemo.find((o) => String(o.value) === "") ??
      franchiseOptionsMemo.find(
        (o) => String(o.label).toLowerCase() === "all"
      ) ??
      franchiseOptionsMemo[0] ??
      null
    );
  }, [franchiseOptionsMemo]);

  const carrierOptionsMemo = useMemo<Option[]>(
    () =>
      Array.isArray(carrierData) && carrierData.length
        ? carrierData.map(mapCarrier)
        : carrierOptions ?? [],
    [carrierData, carrierOptions, mapCarrier]
  );

  const officeOptionsMemo = useMemo<Option[]>(
    () =>
      Array.isArray(officeData) && officeData.length
        ? officeData.map(mapOffice)
        : officeOptions ?? [],
    [officeData, officeOptions, mapOffice]
  );

  const acquisitionOptionsMemo = useMemo<Option[]>(
    () =>
      Array.isArray(acquisitionData) && acquisitionData.length
        ? (acquisitionData.map(mapAcquisition).filter(Boolean) as Option[])
        : acquisitionOptions ?? [],
    [acquisitionData, acquisitionOptions, mapAcquisition]
  );

  const brandOptionsMemo = useMemo<Option[]>(() => {
    if (Array.isArray(brandData) && brandData.length) {
      const arr = brandData.map(mapBrand).filter(Boolean) as Option[];
      const seen = new Set<string>();
      const deduped: Option[] = [];
      for (const o of arr) {
        const key = String(o.value);
        if (!seen.has(key)) {
          seen.add(key);
          deduped.push({ ...o, value: key });
        }
      }
      return deduped.sort((a, b) => a.label.localeCompare(b.label));
    }

    return brandOptions ?? [];
  }, [brandData, brandOptions, mapBrand]);

  const stateOptionsMemo = useMemo<Option[]>(() => {
    if (Array.isArray(statesData) && statesData.length) {
      return statesData.map(mapState);
    }
    return stateOptions ?? [];
  }, [statesData, stateOptions, mapState]);

  const inProductionOpts = [
    { value: 1, label: "In Production" },
    { value: 0, label: "Not In Production" },
  ];

  const inProductionOptsMemo = useMemo<Option[]>(
    () =>
      Array.isArray(inProductionOpts) && inProductionOpts.length
        ? inProductionOpts.map(mapInProduction)
        : inProductionOptions ?? [],
    [inProductionOpts, mapInProduction]
  );

  const channelOptionsMemo = useMemo<Option[]>(
    () =>
      Array.isArray(channelData) && channelData.length
        ? channelData.map(mapChannels)
        : channelOptions ?? [],
    [channelData, channelOptions, mapChannels]
  );

  const mapInitialToOption = useCallback(
    (initial: any, optionsList: Option[] | undefined): Option | null => {
      if (initial === undefined || initial === null) return null;
      if (!optionsList?.length) {
        if (
          typeof initial === "object" &&
          "label" in initial &&
          "value" in initial
        )
          return initial as Option;
        return { label: String(initial), value: String(initial) };
      }
      if (
        typeof initial === "object" &&
        "label" in initial &&
        "value" in initial
      )
        return initial as Option;
      const str = String(initial);
      return (
        optionsList.find((o) => o.value === str) ??
        optionsList.find((o) => o.label === str) ??
        null
      );
    },
    []
  );

  const mapInitialToOptions = useCallback(
    (initial: any, optionsList: Option[] | undefined): Option[] => {
      if (!initial) return [];
      if (!optionsList?.length) {
        if (!Array.isArray(initial)) initial = [initial];
        return initial.map((i: any) =>
          typeof i === "object" && "label" in i && "value" in i
            ? i
            : { label: String(i), value: String(i) }
        );
      }
      if (!Array.isArray(initial)) initial = [initial];
      return initial
        .map((i: any) => {
          if (typeof i === "object" && "label" in i && "value" in i)
            return i as Option;
          const str = String(i);
          return (
            optionsList.find((o) => o.value === str) ??
            optionsList.find((o) => o.label === str) ??
            null
          );
        })
        .filter(Boolean) as Option[];
    },
    []
  );

  const [franchise, setFranchise] = useState<SOption | null>(() => {
    const mapped = mapInitialToOption(
      initialFranchise,
      franchiseOptionsMemo
    ) as SOption | null;
    return mapped ?? (defaultFranchiseOption as SOption | null);
  });

  const [carrier, setCarrier] = useState<SOption | null>(
    () =>
      mapInitialToOption(initialCarrier, carrierOptionsMemo) as SOption | null
  );
  const [acquisition, setAcquisition] = useState<SOption | null>(
    () =>
      mapInitialToOption(
        initialAcquisition,
        acquisitionOptionsMemo
      ) as SOption | null
  );

  const [brand, setBrand] = useState<Option[]>(() =>
    mapInitialToOptions(initialBrand, brandOptionsMemo)
  );

  const [offices, setOffices] = useState<Option[]>(() =>
    mapInitialToOptions(initialOffices, officeOptionsMemo)
  );

  const [states, setStates] = useState<Option[]>(() =>
    mapInitialToOptions(initialStates, stateOptionsMemo)
  );

  const [inProduction, setInProduction] = useState<SOption | null>(
    () =>
      mapInitialToOption(
        initialInProduction,
        inProductionOptsMemo
      ) as SOption | null
  );

  const [channels, setChannels] = useState<SOption | null>(
    () =>
      mapInitialToOption(initialChannel, channelOptionsMemo) as SOption | null
  );

  const handleSetFranchise = useCallback(
    (val: SOption | null) => setFranchise(val),
    []
  );
  const handleSetCarrier = useCallback(
    (val: SOption | null) => setCarrier(val),
    []
  );
  const handleSetAcquisition = useCallback(
    (val: SOption | null) => setAcquisition(val),
    []
  );

  const handleSetBrand = useCallback((val: Option[]) => setBrand(val), []);
  const handleSetOffices = useCallback(
    (vals: Option[]) => setOffices(vals),
    []
  );
  const handleSetStates = useCallback((vals: Option[]) => setStates(vals), []);
  const handleSetInProduction = useCallback(
    (vals: SOption | null) => setInProduction(vals),
    []
  );

  const handleSetChannels = useCallback(
    (val: SOption | null) => setChannels(val),
    []
  );

  const handleApply = useCallback(() => {
    const stateCodes: string[] | undefined = states?.length
      ? states
          .map((s) =>
            String(s.value ?? "")
              .trim()
              .toUpperCase()
          )
          .filter((s) => s !== "")
      : undefined;

    const payload: DashboardFiltersPayload = {
      franchiseType: franchise ?? undefined,
      carrier: carrier ?? undefined,
      acquisition: acquisition ?? undefined,
      brand: brand ?? undefined,
      offices: offices.length ? offices : undefined,
      states: stateCodes,
      inProduction: inProduction ?? undefined,
      channel: channels ?? undefined,
    };
    if (onApply) onApply(payload);
  }, [
    franchise,
    carrier,
    acquisition,
    brand,
    offices,
    states,
    inProduction,
    channels,
    onApply,
  ]);

  const handleClear = useCallback(() => {
    setFranchise(defaultFranchiseOption as SOption | null);
    setCarrier(null);
    setAcquisition(null);
    setBrand([]);
    setOffices([]);
    setStates([]);
    setInProduction(null);
    setChannels(null);
    if (onClear) onClear();
  }, [onClear, defaultFranchiseOption]);

  return (
    <div className={`space-y-3 ${className}`}>
      <div
        className="grid grid-cols-1 md:grid-cols-9 gap-3 items-end"
        style={{ gridTemplateColumns: "repeat(8, 1fr) auto" }}
      >
        <SingleSelectInput
          label="Franchise Type"
          options={franchiseOptionsMemo}
          value={franchise}
          onChange={handleSetFranchise}
          placeholder="Select franchise..."
          isClearable
          className="w-full"
        />

        <SingleSelectInput
          label="Carrier"
          options={carrierOptionsMemo}
          value={carrier}
          onChange={handleSetCarrier}
          placeholder="Select carrier..."
          isClearable
          className="w-full"
        />

        <MultiSelectInputDashboard
          label="Office"
          options={officeOptionsMemo}
          value={offices}
          onChange={handleSetOffices}
          placeholder="Select offices..."
          className="w-full"
        />

        <SingleSelectInput
          label="Distribution Channel"
          options={channelOptionsMemo}
          value={channels}
          onChange={handleSetChannels}
          placeholder="Select channel..."
          isClearable
          className="w-full"
        />

        <SingleSelectInput
          label="Acquisition Name"
          options={acquisitionOptionsMemo}
          value={acquisition}
          onChange={handleSetAcquisition}
          placeholder="Select acquisition..."
          isClearable
          className="w-full"
        />

        <div className="">
          <label className="block text-[11px] font-medium text-gray-700 mb-1">
            Brand
          </label>
          <MultiSelectInputDashboard
            options={brandOptionsMemo}
            value={brand}
            onChange={handleSetBrand}
            placeholder="Select brand..."
            className="w-full"
          />
        </div>

        <div>
          <label className="block text-[11px] font-medium text-gray-700 mb-1">
            State
          </label>
          <MultiSelectInputDashboard
            options={stateOptionsMemo}
            value={states}
            onChange={handleSetStates}
            placeholder="Select states..."
            className="w-full"
          />
        </div>

        <SingleSelectInput
          label="Carrier Production Status"
          options={inProductionOptsMemo}
          value={inProduction}
          onChange={handleSetInProduction}
          placeholder="Select ..."
          isClearable
          className="w-full"
        />

        <div className="flex items-center gap-2 justify-self-end pb-1">
          <SearchButton buttonValue={"SEARCH"} onClick={handleApply} />
          <RefreshButton buttonValue={""} onClick={handleClear} />
        </div>
      </div>
    </div>
  );
};

export default DashboardFilterUI;
